import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";

export function BackupCodes({ codes, onDone }) {
  const { t } = usePrefs();
  const copy = async () => { try { await navigator.clipboard.writeText(codes.join("\n")); toast.success(t("linkCopied")); } catch { toast(codes.join("  ")); } };
  return (
    <div className="space-y-4" data-testid="mfa-backup-codes">
      <h3 className="font-display text-xl font-bold">{t("mfaBackupTitle")}</h3>
      <p className="text-sm text-slate-600">{t("mfaBackupText")}</p>
      <div className="grid grid-cols-2 gap-2 rounded-sm bg-slate-50 p-4 font-data text-sm">{codes.map((c) => <span key={c} data-testid="mfa-backup-code">{c}</span>)}</div>
      <Button variant="outline" onClick={copy} className="w-full rounded-sm" data-testid="mfa-copy-codes"><Copy size={14} className="me-1.5" />{t("copyCodes")}</Button>
      {onDone && <Button onClick={onDone} className="h-11 w-full rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="mfa-codes-saved-button">{t("mfaSaved")}</Button>}
    </div>
  );
}

// Second login step for admin/supervision: setup (QR) or verify (code / backup code)
export function MfaStep({ challenge, onSuccess, onCancel }) {
  const { t } = usePrefs();
  const setup = challenge.mfa_setup_required;
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (setup) api.post("/auth/mfa/setup", { mfa_token: challenge.mfa_token }).then(({ data }) => setQr(data)).catch((e) => { toast.error(errMsg(e)); onCancel(); });
  }, [setup, challenge.mfa_token]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(setup ? "/auth/mfa/activate" : "/auth/mfa/verify", { mfa_token: challenge.mfa_token, code });
      if (setup) { setUser(data.user); setCodes(data.backup_codes); } else onSuccess(data.user);
    } catch (err) { toast.error(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };
  if (codes) return <BackupCodes codes={codes} onDone={() => onSuccess(user)} />;
  return (
    <form onSubmit={submit} className="rise space-y-5" data-testid="mfa-step">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0F172A] text-[#E5C46B]"><ShieldCheck size={20} /></span>
        <div><div className="eyebrow">{challenge.email}</div><h2 className="font-display text-2xl font-bold text-slate-900">{setup ? t("mfaSetupTitle") : t("mfaTitle")}</h2></div>
      </div>
      {setup ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">{t("mfaSetupText")}</p>
          {qr ? <>
            <img src={qr.qr} alt="QR" className="mx-auto h-48 w-48 rounded-sm border bg-white p-2" data-testid="mfa-qr" />
            <div className="text-center text-[11px] text-slate-500">{t("mfaManual")} : <span className="select-all font-data font-bold text-slate-800" data-testid="mfa-secret">{qr.secret}</span></div>
          </> : <Loader2 className="mx-auto animate-spin text-slate-400" />}
        </div>
      ) : <p className="text-sm text-slate-600">{backup ? t("mfaBackupText") : t("mfaEnterCode")}</p>}
      <Input autoFocus required value={code} onChange={(e) => setCode(e.target.value)} inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code"
        placeholder={backup ? t("mfaBackupPh") : "123456"} maxLength={backup ? 12 : 6} data-testid="mfa-code-input" className="h-12 rounded-sm bg-white text-center font-data text-xl tracking-[0.3em]" />
      <Button type="submit" disabled={busy} className="h-11 w-full rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="mfa-submit-button">{busy && <Loader2 size={16} className="me-1.5 animate-spin" />}{t("verify")}</Button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-900" data-testid="mfa-cancel-button">{t("back")}</button>
        {!setup && <button type="button" onClick={() => { setBackup(!backup); setCode(""); }} className="font-semibold text-[#996515] hover:underline" data-testid="mfa-toggle-backup">{backup ? t("mfaUseApp") : t("mfaUseBackup")}</button>}
      </div>
    </form>
  );
}
